import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  login as apiLogin,
  verifyLogin as apiVerifyLogin,
  logout as apiLogout,
  getStoredAuth,
  storeAuth,
  checkAuth,
} from '../services/authService';
import { ALLOWED_ROLES } from '../constants/roles';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

const checkUserOutletAccess = (userRoles) => {
  if (!userRoles || !Array.isArray(userRoles)) return false;
  return userRoles.some((role) => {
    const roleName = role?.name || role;
    return ALLOWED_ROLES.includes(roleName);
  });
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasOutletAccess, setHasOutletAccess] = useState(false);

  const checkAuthStatus = useCallback(async () => {
    try {
      const { token, user: storedUser } = getStoredAuth();
      if (token && storedUser) {
        try {
          const result = await checkAuth();
          if (result.success && result.user) {
            setUser(result.user);
            setIsAuthenticated(true);
            setHasOutletAccess(checkUserOutletAccess(result.user.roles));
          } else {
            await apiLogout();
            setUser(null);
            setIsAuthenticated(false);
            setHasOutletAccess(false);
          }
        } catch {
          // Network failure: keep stored user as a soft fallback
          setUser(storedUser);
          setIsAuthenticated(true);
          setHasOutletAccess(checkUserOutletAccess(storedUser.roles));
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setHasOutletAccess(false);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      
      await apiLogout();
      setUser(null);
      setIsAuthenticated(false);
      setHasOutletAccess(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  // Re-check auth when the tab becomes visible (parallels RN AppState 'active').
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        checkAuthStatus();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [isAuthenticated, checkAuthStatus]);

  const handleLogin = useCallback(async (phoneNumber) => apiLogin(phoneNumber), []);

  const handleVerifyLogin = useCallback(async (phoneNumber, otp) => {
    const response = await apiVerifyLogin(phoneNumber, otp);
    if (response.success) {
      storeAuth(response.token, response.refreshToken, response.user);
      setUser(response.user);
      setIsAuthenticated(true);
      setHasOutletAccess(checkUserOutletAccess(response.user.roles));
    }
    return response;
  }, []);

  const handleLogout = useCallback(async () => {
    await apiLogout();
    setUser(null);
    setIsAuthenticated(false);
    setHasOutletAccess(false);
  }, []);

  const refreshAuth = useCallback(async () => {
    await checkAuthStatus();
  }, [checkAuthStatus]);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    hasOutletAccess,
    login: handleLogin,
    verifyLogin: handleVerifyLogin,
    logout: handleLogout,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
